import type { ReactNode } from 'react';
import { Titre } from './Titre';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE GABARIT DE PAGE DE TEXTE RÉUTILISABLE — F-69, ET IL EST NOMMÉ AU CAHIER DES
 * CHARGES COMME UN LIVRABLE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-69 : « **la page juridique existe avec un texte de remplacement**, et le site porte **un
 * gabarit de page de texte réutilisable** : si l'équipe juridique remet plusieurs documents,
 * **le gabarit sert autant de fois qu'il faut sans que le périmètre bouge.** »
 *
 * **CETTE DERNIÈRE PHRASE EST TOUT L'OBJET DU FICHIER, ET ELLE EST CONTRE-INTUITIVE.** La
 * liste des huit pages est **fermée** : ajouter une neuvième page serait un changement de
 * périmètre. Mais « quelles pages la loi exige et ce qu'elles disent restent **hors de cette
 * pipeline** » — donc le nombre de documents juridiques n'est pas connu, et il changera.
 *
 * Le gabarit résout les deux : **un document juridique de plus n'est pas une page de plus**,
 * c'est une section de plus sur la page 7, ou une adresse de plus servie par le même
 * composant. « Ce qui est du périmètre est ce que le site **RÉSERVE**. »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUI N'EST PAS ADMINISTRABLE, ET IL FAUT LE LIRE AVANT DE VOULOIR L'AMÉLIORER.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-69 : « **remplacer ce texte de remplacement par le texte réel est une livraison de
 * code**, faite une fois. **Ce n'est pas une saisie** : rien du site n'est administrable
 * hors les colonnes d'une structure. » Et : « **aucun objet de contenu n'existe** : pas de
 * page comme donnée, pas de bloc, pas de version, pas de brouillon, pas d'état de
 * publication, **personne qui écrit et personne qui publie**. »
 *
 * Ce composant ne prend donc **aucun identifiant, aucune clé, aucune adresse de document**.
 * Il prend du texte, qui vient du code de la page qui l'appelle. Un jour où quelqu'un
 * voudra « juste » lui faire lire un fichier distant, c'est **l'objet de contenu que le
 * modèle a refusé** qui rentrerait par la fenêtre.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE TEXTE DE REMPLACEMENT SE VOIT, ET C'EST UNE PROPRIÉTÉ ET NON UN INACHÈVEMENT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `CDC-6` §13 : « l'équipe juridique n'a pas remis son texte au lancement → **la page
 * juridique est en ligne avec son texte de remplacement.** » Elle est donc en ligne, et le
 * remplacement est **annoncé** plutôt que déguisé en contenu définitif : un texte de
 * remplacement qui a l'air d'un vrai texte est un texte qu'on oublie de remplacer.
 */
type Props = {
  readonly titre: string;
  /**
   * **Vrai tant que le texte livré n'est pas le texte réel.** Il porte alors un bandeau qui
   * le dit — voir le motif ci-dessus.
   */
  readonly enAttenteDuTexteReel?: boolean;
  readonly children: ReactNode;
};

export function PageDeTexte({
  titre,
  enAttenteDuTexteReel = false,
  children,
}: Props) {
  return (
    <article className="px-6 py-12">
      <div className="mx-auto flex max-w-etroite flex-col gap-6">
        <Titre niveau={1}>{titre}</Titre>

        {enAttenteDuTexteReel && (
          // **Un bandeau, parce que c'est « un bloc entier portant une phrase »** — la
          // seule forme que `DESIGN.md` autorise pour un fond teinté. Il n'est pas importé
          // de `Bandeau` : celui-là porte un `role` de statut, destiné à une réponse du
          // serveur, et **cette phrase-ci n'est pas un événement**, c'est un état du
          // document. Un lecteur d'écran l'annoncerait à tort comme une notification.
          <p className="rounded-3 bg-bandeau-attention-fond px-4 py-3 text-14 font-weight-500 leading-normal text-bandeau-attention-texte">
            Ce texte est un texte de remplacement. Le texte définitif sera livré
            avec une mise à jour du site.
          </p>
        )}

        <div className="flex flex-col gap-4">{children}</div>
      </div>
    </article>
  );
}

/**
 * **Une section du gabarit** : un intertitre et son texte. C'est l'unité que « le gabarit
 * sert autant de fois qu'il faut » désigne — un document juridique de plus est une section
 * de plus, jamais une page de plus.
 */
export function SectionDeTexte({
  titre,
  children,
}: {
  readonly titre: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <Titre niveau={2} taille="18">
        {titre}
      </Titre>
      <div className="flex flex-col gap-2 text-16 leading-large text-texte-faible">
        {children}
      </div>
    </section>
  );
}
